import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { deleteAssetsBatch, listAssets, updateAsset, type Asset } from '../api/assets';
import {
  allowedAssetMimeTypes,
  assetNameFromFile,
  maxAssetBatchUploadCount,
  maxAssetFileSizeBytes,
  uploadAssetBatch,
  validateUploadFile,
} from '../utils/asset-upload';
import { isAuthenticated, mustResetPassword, getAdminDisplayName } from '../auth/session';
import { AdminLayout } from '../components/layout';
import {
  AdminEmptySection,
  AdminInput,
  AdminSelect,
  AdminSnackbarAlert,
  AssetsIcon,
  RemoveConfirmationDialog,
  SearchIcon as KitSearchIcon,
  adminCardSx,
} from '../components/ui';
import { fieldLabelSx } from '../components/ui/admin-input';
import { getSnackbarNoticeSeverity } from '../utils/notice-severity';

const assetValidationErrorMessages = [
  'Choose one or more image files.',
  'Upload PNG, JPG, SVG, or WebP images.',
  'File is larger than 10 MB.',
] as const;

function parseTags(value: string) {
  return [
    ...new Set(
      value
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
}

const cardRadius = '12px';

export const Route = createFileRoute('/assets')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' });
    }
    if (mustResetPassword()) {
      throw redirect({ to: '/change-password' });
    }
  },
  component: AssetsPage,
});

function AssetsPage() {
  const queryClient = useQueryClient();
  const displayName = getAdminDisplayName() ?? 'Admin';
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [gridSelectionIds, setGridSelectionIds] = useState<Set<string>>(() => new Set());
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const assetsQuery = useQuery({
    queryKey: ['assets', query, tag],
    queryFn: () => listAssets({ query, tag }),
  });

  /** Tag filter options from assets matching search only — not the active tag filter. */
  const tagOptionsQuery = useQuery({
    queryKey: ['assets', 'tag-options', query],
    queryFn: () => listAssets({ query }),
  });

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const asset of tagOptionsQuery.data ?? []) {
      for (const assetTag of asset.tags) tagSet.add(assetTag);
    }
    if (tag) tagSet.add(tag);
    return [...tagSet].sort((a, b) => a.localeCompare(b));
  }, [tagOptionsQuery.data, tag]);

  const assetCount = assetsQuery.data?.length ?? 0;

  const invalidateAssets = () => queryClient.invalidateQueries({ queryKey: ['assets'] });

  const uploadMutation = useMutation({
    mutationFn: ({
      files,
      tags,
      customName,
    }: {
      files: File[];
      tags: string[];
      customName?: string;
    }) => uploadAssetBatch(files, tags, { customName }),
    onSuccess: async ({ successCount, failures, total }) => {
      setName('');
      setTags('');
      setPendingFiles([]);
      setFileInputKey((current) => current + 1);
      if (failures.length > 0) {
        setMessage(`Uploaded ${successCount} of ${total}. Failed: ${failures.join(', ')}.`);
      } else {
        setMessage(successCount === 1 ? 'Asset uploaded.' : `Uploaded ${successCount} assets.`);
      }
      await invalidateAssets();
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'Upload failed.'),
  });

  const updateMutation = useMutation({
    mutationFn: updateAsset,
    onSuccess: async (asset) => {
      setSelectedAsset(asset);
      setMessage('Asset updated.');
      await invalidateAssets();
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'Update failed.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => deleteAssetsBatch(ids),
    onSuccess: async (result, ids) => {
      setDeleteConfirmOpen(false);
      setPendingDeleteIds([]);
      setGridSelectionIds(new Set());
      if (selectedAsset && ids.includes(selectedAsset.id)) {
        setSelectedAsset(null);
        setName('');
        setTags('');
      }
      if (result.failures.length > 0) {
        setMessage(
          `Deleted ${result.deletedCount} of ${result.deletedCount + result.failures.length}.`,
        );
      } else {
        setMessage(
          result.deletedCount === 1 ? 'Asset deleted.' : `Deleted ${result.deletedCount} assets.`,
        );
      }
      await invalidateAssets();
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'Delete failed.'),
  });

  function handleUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);

    if (pendingFiles.length === 0) {
      setMessage('Choose one or more image files.');
      return;
    }

    if (pendingFiles.length > maxAssetBatchUploadCount) {
      setMessage(`A maximum of ${maxAssetBatchUploadCount} files can be uploaded at once.`);
      return;
    }

    for (const file of pendingFiles) {
      const validationError = validateUploadFile(
        file,
        allowedAssetMimeTypes,
        maxAssetFileSizeBytes,
      );
      if (validationError) {
        setMessage(`${file.name}: ${validationError}`);
        return;
      }
    }

    uploadMutation.mutate({
      files: pendingFiles,
      tags: parseTags(tags),
      customName: pendingFiles.length === 1 ? name : undefined,
    });
  }

  function startEditing(asset: Asset) {
    setSelectedAsset(asset);
    setName(asset.name);
    setTags(asset.tags.join(', '));
    setPendingFiles([]);
    setMessage(null);
  }

  function saveSelectedAsset() {
    if (!selectedAsset) return;
    updateMutation.mutate({
      id: selectedAsset.id,
      name,
      tags: parseTags(tags),
    });
  }

  function deleteGridSelection() {
    if (gridSelectionIds.size === 0) return;
    setPendingDeleteIds([...gridSelectionIds]);
    setDeleteConfirmOpen(true);
  }

  const toggleGridSelection = useCallback((assetId: string) => {
    setGridSelectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  }, []);

  const selectableAssetIds = useMemo(
    () => (assetsQuery.data ?? []).map((asset) => asset.id),
    [assetsQuery.data],
  );

  const gridSelectionCount = gridSelectionIds.size;
  const allGridSelected =
    selectableAssetIds.length > 0 && selectableAssetIds.every((id) => gridSelectionIds.has(id));

  const selectAllOnGrid = useCallback(() => {
    setGridSelectionIds(new Set(selectableAssetIds));
  }, [selectableAssetIds]);

  const clearGridSelection = useCallback(() => {
    setGridSelectionIds(new Set());
  }, []);

  function resetForm() {
    setSelectedAsset(null);
    setName('');
    setTags('');
    setPendingFiles([]);
    setFileInputKey((current) => current + 1);
    setMessage(null);
    setDeleteConfirmOpen(false);
    setGridSelectionIds(new Set());
    setPendingDeleteIds([]);
  }

  const pendingUploadCount = pendingFiles.length;
  const pendingDeleteCount = pendingDeleteIds.length;
  const isMultiFileUpload = !selectedAsset && pendingUploadCount > 1;
  const showUploadNameField = Boolean(selectedAsset) || !isMultiFileUpload;

  const paperShell = (theme: import('@mui/material/styles').Theme) => ({
    ...adminCardSx(theme),
    p: 2.5,
  });

  return (
    <AdminLayout
      title="Asset Library"
      subtitle="Manage reusable image assets and tags"
      displayName={displayName}
      width="wide"
    >
      {/*
        No `maxWidth` / `mx: auto` of its own. `AdminLayout` already owns the
        page container — 1240, or 1440 with `width="wide"` — and a second one
        inside it centred this screen's content in the first, leaving Assets
        inset from both sides while every other screen started at the
        container's edge. Caught on review of CAE-1556.
      */}
      <Stack spacing={2.5}>
        <Paper
          component="form"
          id="asset-form"
          elevation={0}
          onSubmit={(event) => {
            if (selectedAsset) {
              event.preventDefault();
              saveSelectedAsset();
              return;
            }
            handleUpload(event);
          }}
          sx={paperShell}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between', mb: 2.5 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {selectedAsset ? 'Edit asset' : isMultiFileUpload ? 'Batch upload' : 'New asset'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
                {selectedAsset
                  ? 'Update the library details for the selected image.'
                  : isMultiFileUpload
                    ? `Upload up to ${maxAssetBatchUploadCount} images in one request (10 MB each). Names come from file names; tags apply to the whole batch.`
                    : `Upload one or more images (up to ${maxAssetBatchUploadCount} per request, 10 MB each). Names default to file names; you can set a custom name when uploading a single file. Tags apply to the whole upload.`}
              </Typography>
            </Box>
            {selectedAsset ? (
              <Button
                variant="outlined"
                size="small"
                type="button"
                onClick={resetForm}
                sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}
              >
                New upload
              </Button>
            ) : null}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              alignItems: 'start',
              gridTemplateColumns: {
                xs: '1fr',
                sm:
                  selectedAsset || showUploadNameField
                    ? 'repeat(2, minmax(0, 1fr))'
                    : 'repeat(2, minmax(0, 1fr))',
                lg: !selectedAsset && showUploadNameField ? 'repeat(3, minmax(0, 1fr))' : undefined,
              },
            }}
          >
            {showUploadNameField ? (
              <AdminInput
                label="Name"
                value={name}
                onChange={setName}
                placeholder={
                  selectedAsset
                    ? 'e.g. Player Jersey Red'
                    : pendingUploadCount === 1
                      ? `Optional — defaults to ${assetNameFromFile(pendingFiles[0]!)}`
                      : 'Optional — uses file name when you choose an image'
                }
                autoComplete="off"
                ignorePasswordManagers
              />
            ) : null}
            <AdminInput
              label="Tags"
              value={tags}
              onChange={setTags}
              placeholder="coin, gold, red"
            />
            {!selectedAsset ? (
              <Stack spacing="5px" sx={{ minWidth: 0 }}>
                {/*
                  The file picker cannot be an `AdminInput` — it is a native
                  file input behind a button — so it borrows the kit's field
                  label directly, the way `prize-tier-fields.tsx` does, rather
                  than sitting beside NAME and TAGS in a different voice.
                */}
                <Box component="span" sx={fieldLabelSx}>
                  Images
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    minHeight: 40,
                    boxSizing: 'border-box',
                  }}
                >
                  <Button
                    component="label"
                    htmlFor="asset-file-input"
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0, height: 40 }}
                  >
                    Choose files
                  </Button>
                  <input
                    key={fileInputKey}
                    id="asset-file-input"
                    hidden
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={(event) => {
                      const chosen = event.target.files;
                      const files = chosen ? [...chosen] : [];
                      setPendingFiles(files);
                      if (files.length > 1) {
                        setName('');
                      }
                    }}
                  />
                  <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                      {pendingUploadCount === 0
                        ? 'No files chosen'
                        : `${pendingUploadCount} ${pendingUploadCount === 1 ? 'file' : 'files'} selected`}
                    </Typography>
                    {isMultiFileUpload ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.35 }}
                      >
                        Names:{' '}
                        {pendingFiles
                          .map((f) => assetNameFromFile(f))
                          .slice(0, 4)
                          .join(', ')}
                        {pendingUploadCount > 4 ? ` (+${pendingUploadCount - 4} more)` : ''}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Stack>
            ) : null}
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{
              mt: 2.5,
              pt: 2.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              justifyContent: 'flex-end',
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            {selectedAsset ? (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={uploadMutation.isPending || updateMutation.isPending}
                sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save asset'}
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={uploadMutation.isPending || pendingUploadCount === 0}
                startIcon={<CloudUploadOutlinedIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2.5,
                  alignSelf: { xs: 'stretch', sm: 'flex-end' },
                }}
              >
                {uploadMutation.isPending
                  ? 'Uploading…'
                  : pendingUploadCount > 1
                    ? `Upload ${pendingUploadCount} assets`
                    : pendingUploadCount === 1
                      ? 'Upload asset'
                      : 'Upload assets'}
              </Button>
            )}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={paperShell}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              alignItems: 'start',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 220px)' },
            }}
          >
            <AdminInput
              label="Search"
              value={query}
              onChange={setQuery}
              placeholder="Search by name"
              icon={<KitSearchIcon />}
            />
            {/*
              `AdminSelect` already models "no filter" as its empty option, which
              is what this screen's `''` tag means — so `allLabel` carries the
              wording the old `renderValue` produced.
            */}
            <AdminSelect
              label="Tag"
              value={tag}
              onChange={setTag}
              allLabel="All tags"
              options={allTags.map((assetTag) => ({ value: assetTag, label: assetTag }))}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...paperShell, p: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 2.5 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Images
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              useFlexGap
              sx={{ flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}
            >
              {assetCount > 0 ? (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={selectAllOnGrid}
                    disabled={selectableAssetIds.length === 0 || allGridSelected}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Select all
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={clearGridSelection}
                    disabled={gridSelectionCount === 0}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Clear selection
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={gridSelectionCount === 0 || deleteMutation.isPending}
                    onClick={deleteGridSelection}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                  >
                    Delete selected{gridSelectionCount > 0 ? ` (${gridSelectionCount})` : ''}
                  </Button>
                </>
              ) : null}
              <Chip
                label={`${assetCount} ${assetCount === 1 ? 'asset' : 'assets'}`}
                size="small"
                sx={(theme) => ({
                  fontWeight: 600,
                  bgcolor: theme.tokens.componentColor.countBadgeBg,
                  color: theme.tokens.componentColor.countBadgeText,
                  border: theme.tokens.border.default,
                  height: 28,
                  '& .MuiChip-label': { px: 1.5 },
                })}
              />
            </Stack>
          </Stack>

          {assetsQuery.isLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Loading assets…
            </Typography>
          ) : null}
          {assetsQuery.isError ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Unable to load assets.
            </Typography>
          ) : null}
          {assetsQuery.data?.length === 0 && !assetsQuery.isLoading ? (
            <Box sx={{ py: 1 }}>
              {/*
                `view` is the tone with no action: this grid's own upload
                control is in the panel above it, and an empty state that
                offered a second one would put two "add an asset" buttons a
                few hundred pixels apart.
              */}
              <AdminEmptySection
                tone="view"
                icon={<AssetsIcon />}
                title={query || tag ? 'No assets match your filters' : 'No assets yet'}
                subtitle={
                  query || tag
                    ? 'Nothing in the library matches what you are filtering on. Try a different term, or clear the filters.'
                    : 'Upload an image above to start the library. Assets are reused across contests, so anything added here is available everywhere.'
                }
              />
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              alignItems: 'stretch',
              gridTemplateColumns: {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            {assetsQuery.data?.map((asset) => {
              const isEditing = selectedAsset?.id === asset.id;
              const isGridSelected = gridSelectionIds.has(asset.id);
              return (
                <Box
                  key={asset.id}
                  sx={(theme) => ({
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: '100%',
                    minWidth: 0,
                    bgcolor: 'background.paper',
                    borderRadius: cardRadius,
                    overflow: 'hidden',
                    boxShadow: theme.tokens.shadow.card,
                    outline:
                      isEditing || isGridSelected
                        ? `2px solid ${isEditing ? theme.palette.primary.main : theme.palette.error.main}`
                        : 'none',
                    outlineOffset: 2,
                    transition: theme.transitions.create(['box-shadow', 'outline'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                    '&:hover': {
                      boxShadow: theme.tokens.shadow.tabSelected,
                    },
                  })}
                >
                  <Checkbox
                    size="small"
                    checked={isGridSelected}
                    onChange={() => toggleGridSelection(asset.id)}
                    onClick={(event) => event.stopPropagation()}
                    slotProps={{
                      input: { 'aria-label': `Select ${asset.name} for deletion` } as const,
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      p: 0.25,
                    }}
                  />
                  <Box
                    component="button"
                    type="button"
                    onClick={() => startEditing(asset)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      height: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      p: 0,
                      m: 0,
                      minWidth: 0,
                      border: 'none',
                      bgcolor: 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        flex: '0 0 auto',
                        width: '100%',
                        height: { xs: 220, sm: 200, md: 190, lg: 200 },
                        bgcolor: 'grey.100',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderTopLeftRadius: cardRadius,
                        borderTopRightRadius: cardRadius,
                      }}
                    >
                      {asset.previewUrl ? (
                        <Box
                          component="img"
                          src={asset.previewUrl}
                          alt={asset.name}
                          loading="lazy"
                          sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            width: 'auto',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          align="center"
                          sx={{ px: 1 }}
                        >
                          Preview unavailable
                        </Typography>
                      )}
                    </Box>
                    <Stack spacing={1.5} sx={{ p: 2, flex: '1 1 auto', minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: 'text.primary' }}
                        noWrap
                      >
                        {asset.name}
                      </Typography>
                      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {asset.tags.map((assetTag) => (
                          <Chip
                            key={assetTag}
                            label={assetTag}
                            size="small"
                            sx={(t) => ({
                              height: 24,
                              fontWeight: 600,
                              fontSize: '11px',
                              bgcolor: t.tokens.color.accentSoft,
                              color: t.tokens.color.accentStrong,
                              border: 'none',
                              '& .MuiChip-label': { px: 1 },
                            })}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Stack>

      <RemoveConfirmationDialog
        open={isDeleteConfirmOpen}
        title={pendingDeleteCount > 1 ? 'Delete selected assets?' : 'Delete asset?'}
        titleId="delete-asset-dialog-title"
        contentId="delete-asset-dialog-description"
        isPending={deleteMutation.isPending}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteIds([]);
        }}
        onConfirm={() => {
          if (pendingDeleteIds.length === 0) return;
          deleteMutation.mutate(pendingDeleteIds);
        }}
        confirmLabel={
          pendingDeleteCount > 1 ? `Delete ${pendingDeleteCount} assets` : 'Delete asset'
        }
        pendingConfirmLabel="Deleting…"
      >
        <Typography variant="body2" color="text.secondary">
          {pendingDeleteCount > 1
            ? `This will remove ${pendingDeleteCount} assets from the library. This action cannot be undone.`
            : pendingDeleteCount === 1
              ? `This will remove "${
                  assetsQuery.data?.find((asset) => asset.id === pendingDeleteIds[0])?.name ??
                  'this asset'
                }" from the asset library. This action cannot be undone.`
              : 'This will remove the selected assets from the asset library.'}
        </Typography>
      </RemoveConfirmationDialog>
      <AdminSnackbarAlert
        open={Boolean(message)}
        message={message}
        severity={getSnackbarNoticeSeverity(message, {
          errorMessages: assetValidationErrorMessages,
        })}
        onClose={() => setMessage(null)}
      />
    </AdminLayout>
  );
}
